import { useState, type ReactNode } from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import {
  AppBar,
  Avatar,
  Badge,
  BottomNavigation,
  BottomNavigationAction,
  Box,
  Divider,
  GlobalStyles,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import AccountBalanceWallet from '@mui/icons-material/AccountBalanceWallet';
import BadgeIcon from '@mui/icons-material/Badge';
import Campaign from '@mui/icons-material/Campaign';
import Groups from '@mui/icons-material/Groups';
import Home from '@mui/icons-material/Home';
import Logout from '@mui/icons-material/Logout';
import MoreHoriz from '@mui/icons-material/MoreHoriz';
import Person from '@mui/icons-material/Person';
import Assignment from '@mui/icons-material/Assignment';
import { brand } from '../config/appConfig';
import { useAuth } from '../context/AuthContext';
import { useMisNovedades } from '../hooks/useNovedades';
import { useMisTramites } from '../hooks/useTramites';

interface NavItem {
  to: string;
  label: string;
  icon: ReactNode;
  badge?: number;
}

export default function Layout({ children }: { children: ReactNode }) {
  const theme = useTheme();
  const desktop = useMediaQuery(theme.breakpoints.up('md'));
  const { ficha, signOut } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { data: novedades } = useMisNovedades();
  const { data: tramites } = useMisTramites();
  const [moreAnchor, setMoreAnchor] = useState<HTMLElement | null>(null);

  const sinLeer = (novedades ?? []).filter((n) => !n.leida).length;
  const conPedido = (tramites ?? []).filter((t) => t.estado === 'falta_informacion').length;

  const items: NavItem[] = [
    { to: '/', label: 'Inicio', icon: <Home /> },
    { to: '/tramites', label: 'Trámites', icon: <Assignment />, badge: conPedido },
    { to: '/novedades', label: 'Novedades', icon: <Campaign />, badge: sinLeer },
    { to: '/aportes', label: 'Mis aportes', icon: <AccountBalanceWallet /> },
    { to: '/datos', label: 'Mis datos', icon: <Person /> },
    { to: '/familia', label: 'Grupo familiar', icon: <Groups /> },
    { to: '/credencial', label: 'Credencial', icon: <BadgeIcon /> },
  ];
  const activo = (to: string) => (to === '/' ? pathname === '/' : pathname.startsWith(to));
  const iniciales = ficha ? `${ficha.nombre.charAt(0)}${ficha.apellido.charAt(0)}`.toUpperCase() : '';

  const handleSalir = async () => {
    await signOut();
    navigate('/login');
  };

  // En el celular: 4 accesos fijos + "Más" con el resto
  const fijos = items.slice(0, 4);
  const resto = items.slice(4);
  const bottomValue = fijos.findIndex((i) => activo(i.to));

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <GlobalStyles styles={{ '@media print': { '.no-print': { display: 'none !important' }, body: { background: '#fff' } } }} />
      <AppBar position="sticky" color="inherit" elevation={0} className="no-print" sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Toolbar sx={{ gap: 1.5 }}>
          <Avatar sx={{ bgcolor: 'primary.main', width: 34, height: 34, fontSize: 15 }}>AT</Avatar>
          <Typography variant="subtitle1" fontWeight={700} sx={{ flexGrow: 1 }} noWrap>
            {brand.clientName ? `${brand.clientName} · Afiliados` : 'Afiliados'}
          </Typography>
          {ficha && (
            <Typography variant="body2" color="text.secondary" sx={{ display: { xs: 'none', sm: 'block' } }} noWrap>
              {ficha.nombre} {ficha.apellido}
            </Typography>
          )}
          <Avatar sx={{ width: 32, height: 32, fontSize: 13 }} aria-label="Tu usuario">
            {iniciales}
          </Avatar>
          {desktop && (
            <IconButton aria-label="Salir" onClick={handleSalir}>
              <Logout />
            </IconButton>
          )}
        </Toolbar>
      </AppBar>

      <Box sx={{ display: 'flex', maxWidth: 1280, mx: 'auto' }}>
        {desktop && (
          <Box component="nav" aria-label="Secciones" className="no-print" sx={{ width: 230, flexShrink: 0, p: 2, pr: 0 }}>
            <List disablePadding sx={{ position: 'sticky', top: 80 }}>
              {items.map((i) => (
                <ListItemButton key={i.to} component={RouterLink} to={i.to} selected={activo(i.to)} sx={{ borderRadius: 2, mb: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <Badge badgeContent={i.badge} color="warning">
                      {i.icon}
                    </Badge>
                  </ListItemIcon>
                  <ListItemText primary={i.label} primaryTypographyProps={{ fontWeight: activo(i.to) ? 700 : 500 }} />
                </ListItemButton>
              ))}
              <Divider sx={{ my: 1 }} />
              <ListItemButton onClick={handleSalir} sx={{ borderRadius: 2 }}>
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <Logout />
                </ListItemIcon>
                <ListItemText primary="Salir" />
              </ListItemButton>
            </List>
          </Box>
        )}
        <Box component="main" sx={{ flexGrow: 1, minWidth: 0, p: { xs: 2, md: 3 }, pb: { xs: 11, md: 3 } }}>
          {children}
        </Box>
      </Box>

      {!desktop && (
        <Paper elevation={8} className="no-print" sx={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 1100 }}>
          <BottomNavigation showLabels value={bottomValue >= 0 ? bottomValue : resto.some((i) => activo(i.to)) ? 4 : false}>
            {fijos.map((i) => (
              <BottomNavigationAction
                key={i.to}
                label={i.label}
                component={RouterLink}
                to={i.to}
                icon={
                  <Badge badgeContent={i.badge} color="warning">
                    {i.icon}
                  </Badge>
                }
                sx={{ minWidth: 0, px: 0.5 }}
              />
            ))}
            <BottomNavigationAction
              label="Más"
              icon={<MoreHoriz />}
              onClick={(e) => setMoreAnchor(e.currentTarget)}
              sx={{ minWidth: 0, px: 0.5 }}
            />
          </BottomNavigation>
          <Menu anchorEl={moreAnchor} open={!!moreAnchor} onClose={() => setMoreAnchor(null)} anchorOrigin={{ vertical: 'top', horizontal: 'right' }} transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
            {resto.map((i) => (
              <MenuItem key={i.to} component={RouterLink} to={i.to} selected={activo(i.to)} onClick={() => setMoreAnchor(null)}>
                <ListItemIcon>{i.icon}</ListItemIcon>
                {i.label}
              </MenuItem>
            ))}
            <Divider />
            <MenuItem
              onClick={() => {
                setMoreAnchor(null);
                void handleSalir();
              }}
            >
              <ListItemIcon>
                <Logout />
              </ListItemIcon>
              Salir
            </MenuItem>
          </Menu>
        </Paper>
      )}
    </Box>
  );
}
